import React, { useState, useMemo } from 'react';
import Footer from "../views/Footer";
import FadeIn from "../components/FadeIn";
import LeadCaptureModal from "../components/LeadCaptureModal";
import { Container, Typography, Card, Button } from "../components/ui";
import { courseCategories } from "../data";
import { getCourseImage } from '../config/courseImages';
import { useNavigate, useSearchParams } from "react-router-dom";

const Courses = () => {
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchQuery = (searchParams.get('q') || '').trim().toLowerCase();
  const viewParam = searchParams.get('view');

  // Default to Popular Courses, but show All Courses when view=all query param is present
  const defaultCategory = viewParam === 'all' ? 'all' : 'popular-courses';
  const [activeCategory, setActiveCategory] = useState(defaultCategory);

  const openLeadModal = () => setShowLeadModal(true);
  const closeLeadModal = () => {
    setShowLeadModal(false);
    setSelectedCourse(null);
  };

  // Create categories from courseCategories with Popular Courses first
  const categories = [
    { id: 'all', name: 'All Courses' },
    { id: 'popular-courses', name: 'Popular Courses', featured: true },
    ...courseCategories.filter(cat => cat.category !== 'Popular Courses').map(cat => ({
      id: cat.category.toLowerCase().replace(/\s+/g, '-'),
      name: cat.category
    }))
  ];

  // Flatten all courses from all categories
  const allCourses = courseCategories.flatMap(cat =>
    cat.courses.map(course => ({
      ...course,
      category: cat.category.toLowerCase().replace(/\s+/g, '-'),
      categoryName: cat.category
    }))
  );

  // Deduplicate courses for "All Courses" view by title
  const uniqueCourses = (() => {
    const seen = new Set();
    return allCourses.filter(course => {
      if (seen.has(course.title)) {
        return false;
      }
      seen.add(course.title);
      return true;
    });
  })();

  const filteredCourses = activeCategory === 'all'
    ? uniqueCourses
    : allCourses.filter(course => course.category === activeCategory);

  const displayCourses = useMemo(() => {
    if (!searchQuery) return filteredCourses;
    return uniqueCourses.filter((course) => {
      const title = (course.title || '').toLowerCase();
      const desc = (course.description || '').toLowerCase();
      const cat = (course.categoryName || '').toLowerCase();
      return title.includes(searchQuery) || desc.includes(searchQuery) || cat.includes(searchQuery);
    });
  }, [filteredCourses, uniqueCourses, searchQuery]);

  return (
    <>
      {/* Hero Section */}
      <Container.Section padding="hero" background="gradient">
        <Container size="standard">
          <FadeIn delay={0.2} direction="down" fullWidth>
            <Typography.SectionHeader
              title={
                <>
                  BIM BYTES SOLUTIONS – <span className="text-gradient">COURSE CATALOGUE</span>
                </>
              }
              description="Comprehensive BIM training programs covering Mechanical, Electrical, Plumbing, Fire Fighting, Advanced BIM, Automation, Architecture, and Structural disciplines."
            />
          </FadeIn>
          <FadeIn delay={0.3} direction="up" fullWidth>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <div className="px-4 py-2 bg-white/20 backdrop-blur rounded-full text-white text-sm font-bold border border-white/30 animate-pulse">
                JOB WITHIN 90 DAYS
              </div>
              <div className="px-4 py-2 bg-white/20 backdrop-blur rounded-full text-white text-sm font-bold border border-white/30">
                MAKING OPPORTUNITIES KNOCK ON YOUR DOOR
              </div>
            </div>
          </FadeIn>
          {searchQuery && (
            <FadeIn delay={0.25} direction="down" fullWidth>
              <p className="text-center text-textSecondary mt-4 text-sm sm:text-base">
                Search results for <span className="font-semibold text-textPrimary">&ldquo;{searchParams.get('q')}&rdquo;</span>
                {' — '}
                <button
                  type="button"
                  className="text-primary font-semibold hover:underline"
                  onClick={() => navigate('/courses')}
                >
                  Clear search
                </button>
              </p>
            </FadeIn>
          )}
        </Container>
      </Container.Section>

      {/* Course Categories Filter */}
      <Container.Section padding="compact" background="white">
        <Container size="standard">
          <FadeIn delay={0.3} direction="up" fullWidth>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-8 sm:mb-12">
              {categories.map((category) => (
                <Button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  variant={activeCategory === category.id ? 'primary' : 'ghost'}
                  className={`${activeCategory === category.id ? 'shadow-lg' : 'bg-gray-100 hover:bg-primary/10'} ${category.featured ? 'ring-2 ring-primary/20 font-bold' : ''
                    }`}
                >
                  {category.featured ? '⭐ ' : ''}{category.name}
                </Button>
              ))}
            </div>
          </FadeIn>
        </Container>
      </Container.Section>

      {/* Courses Grid */}
      <Container.Section padding="compact" background="white">
        <Container size="standard">
          <Container.Grid columns="3" gap="default" className="items-stretch [&>*]:h-full">
            {displayCourses.map((course, index) => (
              <FadeIn key={course.id} delay={0.1 + index * 0.05} direction="up">
                <Card variant="elevated" padding="none" className="group overflow-hidden flex flex-col h-full">
                  <img
                    src={getCourseImage(course.title)}
                    alt={course.title}
                    className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105 shrink-0"
                  />
                  <div className="p-6 flex flex-col flex-grow">
                    <Card.Header className="flex-grow">
                      <div className="mb-3 shrink-0">
                        <span className="inline-block bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full">
                          {course.categoryName}
                        </span>
                      </div>
                      <Card.Title
                        size="default"
                        className="line-clamp-2 cursor-pointer text-primary shrink-0"
                        onClick={() =>
                          navigate(`/courses/${course.title.toLowerCase().replace(/\s+/g, "-")}`)
                        }
                      >
                        {course.title}
                      </Card.Title>
                      <Card.Content>
                        <Typography.BodySmall color="secondary" className="line-clamp-3">
                          {course.description}
                        </Typography.BodySmall>
                      </Card.Content>
                    </Card.Header>

                    <div className="flex justify-end shrink-0">
                      <Button
                        onClick={() => {
                          setSelectedCourse(course);
                          openLeadModal();
                        }}
                        variant="primary"
                        size="small"
                      >
                        Enquire Now
                      </Button>
                    </div>
                  </div>
                </Card>
              </FadeIn>
            ))}
          </Container.Grid>
        </Container>
      </Container.Section>



      {/* CTA Section */}
      <Container.Section padding="content" background="primary">
        <Container size="narrow">
          <FadeIn delay={0.2} direction="up" fullWidth>
            <div className="text-center">
              <Typography.H2 color="white" className="mb-4">
                Ready to Start Your BIM Journey?
              </Typography.H2>
              <Typography.Lead color="white" className="mb-6 opacity-90">
                Choose the right course for your career goals and get started with expert-led training
              </Typography.Lead>
              <Button onClick={openLeadModal} variant="white" size="large">
                Book Free Counselling
              </Button>
            </div>
          </FadeIn>
        </Container>
      </Container.Section>

      <Footer />

      <LeadCaptureModal
        isOpen={showLeadModal}
        onClose={closeLeadModal}
        selectedCourse={selectedCourse}
      />
    </>
  );
};

export default Courses;
